import {
	SCHEMA_ANY_COMP_PROP,
	SCHEMA_BOOL_COMP_PROP,
	SCHEMA_NUM_COMP_PROP,
	SCHEMA_STRING_COMP_PROP,
} from '../../constants';
import {
	ComponentPropertyDefinition,
	ComponentPropertyEditor,
	ComponentPropertyGroup,
	ComponentStylePropertyDefinition,
} from '../../types/common';
import { COMMON_COMPONENT_PROPERTIES, COMPONENT_STYLE_GROUP_PROPERTIES } from '../util/properties';

const propertiesDefinition: Array<ComponentPropertyDefinition> = [
	{
		name: 'apiKey',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Google Maps API key',
		description:
			'Browser key for the Google Maps JavaScript API. Without one the component shows the "no map" text and nothing else.',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'centerLat',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Center latitude',
		description: 'Where the map opens when no point is picked yet.',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 20.5937,
	},
	{
		name: 'centerLng',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Center longitude',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 78.9629,
	},
	{
		name: 'zoom',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Zoom',
		description: 'Zoom when no point is picked yet (a picked point opens closer).',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 5,
	},
	{
		name: 'radiusKm',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Radius (km)',
		description:
			'Draws a circle of this radius around the picked point. Empty or 0: no circle.',
		group: ComponentPropertyGroup.BASIC,
	},
	{
		name: 'places',
		schema: SCHEMA_ANY_COMP_PROP,
		displayName: 'Places to show',
		description:
			'Optional list of places drawn on the map (not picked): [{name, lat, lng, radiusKm (or radius + distanceUnit KILOMETER / MILE), excluded}]. A place with lat / lng is drawn there, one with only a name is looked up by name. With a radius it is a circle, without one the area the place covers is outlined. The map fits them all when the list changes.',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'excludedPlaces',
		schema: SCHEMA_ANY_COMP_PROP,
		displayName: 'Excluded places to show',
		description:
			'Optional list of places drawn in the excluded colour, same shape as Places to show.',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'suggestedPlaces',
		schema: SCHEMA_ANY_COMP_PROP,
		displayName: 'Suggested places to show',
		description:
			'Optional list of places offered but not chosen, drawn lighter in the suggested colour; same shape as Places to show.',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'mapId',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Map ID',
		description:
			'A Google Maps map id whose style has the boundary layers on (locality, postal code, district, state, country). With it, a place that carries a Google placeId (and no radius) is drawn with its real outline, without looking it up again. Without it, places are circles and boxes.',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'matchPlaceType',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Match place type',
		description:
			'Check that a place looked up by name is the kind of place its "type" says (a Google Ads or Meta target type: CITY, REGION, POSTAL_CODE, NEIGHBORHOOD, ...). A match is outlined (its real outline with a map id, else the box of its bounds) or, when the lookup has no bounds, marked with a marker; a lookup that finds another kind of place (a state for an unknown postal code) is not drawn. Places with lat / lng or a radius are drawn as usual.',
		group: ComponentPropertyGroup.DATA,
		defaultValue: false,
	},
	{
		name: 'placeColor',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Place colour',
		description: 'Outline and fill colour of the places to show.',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: '#1a1a1a',
	},
	{
		name: 'excludedColor',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Excluded place colour',
		description: 'Outline and fill colour of the places marked excluded.',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: '#c0362c',
	},
	{
		name: 'suggestedColor',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Suggested place colour',
		description:
			'Outline and fill colour of the suggested places (drawn lighter than the chosen ones).',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: '#6b7280',
	},
	{
		name: 'mapLook',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Map look',
		description:
			'Standard is Google’s own look. Grey is a quiet grey map (light grey land, darker water, no roads, places of interest or transit), so the drawn places stand out. Dark grey is the same for a dark page (dark grey land, light names, dark place tags). A Map ID brings its own style, so the look applies only without one; with a Map ID, Dark grey asks for the map style’s dark colour scheme.',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 'STANDARD',
		enumValues: [
			{ name: 'STANDARD', displayName: 'Standard', description: 'Google’s own look' },
			{ name: 'GREY', displayName: 'Grey', description: 'A quiet grey map' },
			{
				name: 'GREY_DARK',
				displayName: 'Dark grey',
				description: 'A quiet dark grey map, for a dark page',
			},
		],
	},
	{
		name: 'placeLabels',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Place labels',
		description:
			'Show each chosen or excluded place’s name in a small tag at its centre (the part of the name before the first comma; "Pin" for a dropped pin). Suggested places get none.',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: false,
	},
	{
		name: 'onPlaceClick',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'On place click',
		editor: ComponentPropertyEditor.EVENT_SELECTOR,
		description:
			'Event run when a drawn place (chosen, suggested or excluded) is clicked; the place, as given, is written to the Clicked place binding first.',
		group: ComponentPropertyGroup.EVENTS,
	},
	{
		name: 'noMapText',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'No map text',
		description: 'Shown when there is no API key or the map could not load.',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 'The map is not available.',
		translatable: true,
	},
	COMMON_COMPONENT_PROPERTIES.readOnly,
	COMMON_COMPONENT_PROPERTIES.onChange,
	COMMON_COMPONENT_PROPERTIES.visibility,
];

const stylePropertiesDefinition: ComponentStylePropertyDefinition = {
	'': [
		COMPONENT_STYLE_GROUP_PROPERTIES.layout.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.border.type,
	],
};

export { propertiesDefinition, stylePropertiesDefinition };
